import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createInvite,
  fetchAuditLog,
  fetchInvites,
  fetchMembers,
  fetchOrg,
  fetchUsage,
  removeMember,
  revokeInvite,
  updateMemberRole,
  updateOrg,
  type OrgInvite,
  type OrgMember,
  type OrgOverview,
  type OrgUsage,
} from '../services/admin';

export const adminKeys = {
  root: ['admin'] as const,
  org: () => [...adminKeys.root, 'org'] as const,
  members: () => [...adminKeys.root, 'members'] as const,
  invites: () => [...adminKeys.root, 'invites'] as const,
  usage: () => [...adminKeys.root, 'usage'] as const,
  audit: (limit: number, offset: number) =>
    [...adminKeys.root, 'audit', limit, offset] as const,
};

export const useOrg = () =>
  useQuery<OrgOverview>(adminKeys.org(), fetchOrg, { retry: false });

export const useUpdateOrg = () => {
  const client = useQueryClient();
  return useMutation(updateOrg, {
    onSuccess: () => {
      client.invalidateQueries(adminKeys.org());
    },
  });
};

export const useMembers = () =>
  useQuery<{ members: OrgMember[] }>(adminKeys.members(), fetchMembers, {
    retry: false,
  });

export const useUpdateMemberRole = () => {
  const client = useQueryClient();
  return useMutation(
    ({ userId, role }: { userId: string; role: string }) =>
      updateMemberRole(userId, role),
    {
      onSuccess: () => {
        client.invalidateQueries(adminKeys.members());
      },
    },
  );
};

export const useRemoveMember = () => {
  const client = useQueryClient();
  return useMutation((userId: string) => removeMember(userId), {
    onSuccess: () => {
      client.invalidateQueries(adminKeys.members());
      client.invalidateQueries(adminKeys.org());
    },
  });
};

export const useInvites = () =>
  useQuery<{ invites: OrgInvite[] }>(adminKeys.invites(), fetchInvites, {
    retry: false,
  });

export const useCreateInvite = () => {
  const client = useQueryClient();
  return useMutation(createInvite, {
    onSuccess: () => {
      client.invalidateQueries(adminKeys.invites());
    },
  });
};

export const useRevokeInvite = () => {
  const client = useQueryClient();
  return useMutation((inviteId: string) => revokeInvite(inviteId), {
    onSuccess: () => {
      client.invalidateQueries(adminKeys.invites());
    },
  });
};

export const useUsage = () =>
  useQuery<OrgUsage>(adminKeys.usage(), fetchUsage, { retry: false });

export const useAuditLog = (limit = 50, offset = 0) =>
  useQuery(adminKeys.audit(limit, offset), () => fetchAuditLog(limit, offset), {
    retry: false,
  });
